import type { CurrentSession } from '@elite-residence/api-client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Link } from 'react-router';
import { useApiClient } from '../api/ApiClientProvider';
import { type Dictionary, useLocale, useText } from '../i18n/LocaleProvider';
import { Alert, Badge, Field, Loading, QueryFailure, readResponse, useAction } from '../ui/ui';

// Managed reference data (V1-2-05; PD-13.11; BR-PLAT-003): values are
// created, relabelled, disabled and re-enabled; codes never change and values
// are never deleted. Administration needs reference_data.manage (All
// Branches); the server decides.

const KINDS = [
  'country',
  'nationality',
  'identity_type',
  'identity_issuer',
  'unit_type',
  'reason_category',
  'acquisition_source',
] as const;
type Kind = (typeof KINDS)[number];

const KIND_LABELS: Readonly<Record<Kind, Readonly<Record<'ar' | 'en', string>>>> = {
  country: { en: 'Countries', ar: 'الدول' },
  nationality: { en: 'Nationalities', ar: 'الجنسيات' },
  identity_type: { en: 'Identity types', ar: 'أنواع الهوية' },
  identity_issuer: { en: 'Identity issuers', ar: 'جهات إصدار الهوية' },
  unit_type: { en: 'Unit types', ar: 'أنواع الوحدات' },
  reason_category: { en: 'Reason categories', ar: 'فئات الأسباب' },
  acquisition_source: { en: 'Acquisition sources', ar: 'مصادر الاستقطاب' },
};

const WORKFLOWS = [
  'price_change',
  'early_exit',
  'cancellation',
  'no_show',
  'deposit_retained',
  'reversal',
  'withdrawn',
  'backdate',
  'needs_correction',
  'maintenance',
  'customer_merge',
  'identity_correction',
  'recipient_change',
  'staff_administration',
] as const;

const text: Dictionary<
  | 'title'
  | 'code'
  | 'labelAr'
  | 'labelEn'
  | 'add'
  | 'disable'
  | 'enable'
  | 'disabled'
  | 'country'
  | 'workflow'
  | 'codeInUse'
> = {
  en: {
    title: 'Reference data',
    code: 'Code',
    labelAr: 'Arabic',
    labelEn: 'English',
    add: 'Add value',
    disable: 'Disable',
    enable: 'Enable',
    disabled: 'Disabled',
    country: 'Country',
    workflow: 'Workflow',
    codeInUse: 'This code is already used.',
  },
  ar: {
    title: 'البيانات المرجعية',
    code: 'الرمز',
    labelAr: 'عربي',
    labelEn: 'إنجليزي',
    add: 'إضافة قيمة',
    disable: 'تعطيل',
    enable: 'تفعيل',
    disabled: 'معطّل',
    country: 'الدولة',
    workflow: 'سير العمل',
    codeInUse: 'هذا الرمز مستخدم بالفعل.',
  },
};

export function ReferencePage({ session }: { readonly session: CurrentSession }) {
  const client = useApiClient();
  const t = useText(text);
  const { locale } = useLocale();
  const queries = useQueryClient();
  const action = useAction();
  const [kind, setKind] = useState<Kind>('unit_type');
  const [form, setForm] = useState({ code: '', ar: '', en: '', countryId: '', workflowCode: 'cancellation' });
  const manage = session.permissions.includes('reference_data.manage') && session.scope.type === 'all_branches';
  const values = useQuery({
    queryKey: ['reference', kind, 'all'],
    queryFn: async ({ signal }) => {
      const r = await client.listReferenceValues({ path: { kind }, query: { includeDisabled: 'true' } }, { signal });
      return readResponse(r).values;
    },
  });
  const countries = useQuery({
    queryKey: ['reference', 'country'],
    enabled: kind === 'identity_issuer',
    queryFn: async ({ signal }) => {
      const r = await client.listReferenceValues({ path: { kind: 'country' } }, { signal });
      return readResponse(r).values;
    },
  });
  const refresh = () => queries.invalidateQueries({ queryKey: ['reference'] });

  async function add(event: FormEvent) {
    event.preventDefault();
    const ok = await action.run(
      (key) =>
        client.createReferenceValue({
          path: { kind },
          headers: { 'idempotency-key': key },
          body: {
            code: form.code,
            labels: { ar: form.ar, en: form.en },
            ...(kind === 'identity_issuer' && form.countryId ? { countryId: form.countryId } : {}),
            ...(kind === 'reason_category' ? { workflowCode: form.workflowCode } : {}),
          },
        }),
      { code_in_use: t.codeInUse },
    );
    if (ok) {
      setForm({ ...form, code: '', ar: '', en: '' });
      await refresh();
    }
  }

  return (
    <main>
      <h1>{t.title}</h1>
      <p>
        <Link to="/finance-reference-data">
          {locale === 'ar' ? 'طرق الدفع وفئات المصروفات' : 'Payment methods and expense categories'}
        </Link>
      </p>
      <div className="er-actions" role="tablist">
        {KINDS.map((k) => (
          <button
            key={k}
            role="tab"
            aria-selected={k === kind}
            className={`er-button${k === kind ? '' : ' secondary'}`}
            type="button"
            onClick={() => setKind(k)}
          >
            {KIND_LABELS[k][locale]}
          </button>
        ))}
      </div>
      {action.error && <Alert kind="error">{action.error}</Alert>}
      <QueryFailure query={countries} />
      <section className="er-card" role="tabpanel">
        {values.isPending ? (
          <Loading />
        ) : values.isError ? (
          <QueryFailure query={values} />
        ) : (
          <table className="er-table">
            <thead>
              <tr>
                <th>{t.code}</th>
                <th>{t.labelAr}</th>
                <th>{t.labelEn}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(values.data ?? []).map((v) => (
                <tr key={v.id}>
                  <td className="er-mono">
                    {v.workflowCode ? `${v.workflowCode} / ` : ''}
                    {v.code}
                  </td>
                  <td dir="rtl">{v.labels.ar}</td>
                  <td dir="ltr">{v.labels.en}</td>
                  <td>
                    {v.disabled && <Badge tone="off">{t.disabled}</Badge>}{' '}
                    {manage && (
                      <button
                        className="er-link-button"
                        type="button"
                        onClick={async () =>
                          (await action.run((key) =>
                            client.changeReferenceValue({
                              path: { kind, id: v.id },
                              headers: { 'idempotency-key': key },
                              body: { disabled: !v.disabled },
                            }),
                          )) && refresh()
                        }
                      >
                        {v.disabled ? t.enable : t.disable}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      {manage && (
        <section className="er-card">
          <h2>{t.add}</h2>
          <form onSubmit={add} noValidate>
            <Field id="ref-code" label={t.code}>
              <input
                id="ref-code"
                dir="ltr"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
              />
            </Field>
            <Field id="ref-ar" label={t.labelAr}>
              <input id="ref-ar" dir="rtl" value={form.ar} onChange={(e) => setForm({ ...form, ar: e.target.value })} />
            </Field>
            <Field id="ref-en" label={t.labelEn}>
              <input id="ref-en" dir="ltr" value={form.en} onChange={(e) => setForm({ ...form, en: e.target.value })} />
            </Field>
            {kind === 'identity_issuer' && (
              <Field id="ref-country" label={t.country}>
                <select
                  id="ref-country"
                  value={form.countryId}
                  onChange={(e) => setForm({ ...form, countryId: e.target.value })}
                >
                  <option value="" />
                  {(countries.data ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.labels[locale]}
                    </option>
                  ))}
                </select>
              </Field>
            )}
            {kind === 'reason_category' && (
              <Field id="ref-workflow" label={t.workflow}>
                <select
                  id="ref-workflow"
                  value={form.workflowCode}
                  onChange={(e) => setForm({ ...form, workflowCode: e.target.value })}
                >
                  {WORKFLOWS.map((w) => (
                    <option key={w} value={w}>
                      {w}
                    </option>
                  ))}
                </select>
              </Field>
            )}
            <button className="er-button" type="submit" disabled={action.busy || !form.code || !form.ar || !form.en}>
              {t.add}
            </button>
          </form>
        </section>
      )}
    </main>
  );
}
